import { useEffect, useState } from "react";
import { Loader2, BarChart3, Eye, Users, MousePointerClick, Smartphone, Monitor, ArrowDown, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { authFetch, API } from "../utils/api";

/** Aggregated first-party analytics dashboard for /admin → Analytics tab. */
export default function AdminAnalytics() {
  const [range, setRange] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (days) => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/analytics?days=${days}`);
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(range); }, [range]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const totals = data.totals || {};
  const funnel = data.funnel || {};
  const fStarted = funnel.signup_started || 0;
  const fStep1 = funnel.signup_step1_done || 0;
  const fStep2 = funnel.signup_step2_done || 0;
  const fDone = funnel.signup_completed || 0;
  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const conversionRate = pct(fDone, fStarted);

  // Build a tiny sparkline-style bar chart from data.timeseries
  const tsMax = (data.timeseries || []).reduce((m, r) => Math.max(m, r.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-analytics-root">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Traffic & funnel</h2>
          <p className="text-sm text-slate-500">First-party analytics · no third parties · last {range} days</p>
        </div>
        <div className="inline-flex rounded-xl bg-slate-100 p-1">
          {[1, 7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${range === d ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`analytics-range-${d}`}
            >
              {d === 1 ? "Today" : `${d}d`}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => load(range)} className="ml-1 h-7 px-2 text-xs">↻</Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Eye} label="Page views" value={totals.page_views || 0} accent="text-[#5BA3C0]" />
        <Tile icon={Users} label="Unique sessions" value={totals.unique_sessions || 0} accent="text-emerald-600" />
        <Tile icon={MousePointerClick} label="Tracked events" value={totals.events || 0} accent="text-amber-600" />
        <Tile icon={BarChart3} label="Signup conversion" value={`${conversionRate}%`} accent="text-fuchsia-600" sub={`${fDone} of ${fStarted} started`} />
      </div>

      {/* Timeseries */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Page views per day</h3>
        {(data.timeseries || []).length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No traffic yet for this range — once visitors land on the site, days will fill in here.</p>
        ) : (
          <div className="flex items-end gap-2 h-32">
            {(data.timeseries || []).map((r) => (
              <div key={r.date} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-gradient-to-t from-[#87CEEB] to-[#6BB8DB] rounded-t-md transition-all hover:opacity-80"
                  style={{ height: `${Math.max(4, (r.count / tsMax) * 100)}%`, minHeight: "4px" }}
                  title={`${r.date}: ${r.count} views`}
                />
                <span className="text-[9px] text-slate-400 font-medium">{r.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signup funnel */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Signup funnel</h3>
        <p className="text-xs text-slate-500 mb-4">Where prospects drop off · unique sessions per stage</p>
        <div className="space-y-2">
          {[
            { id: "signup_started", label: "Signup started", n: fStarted, base: fStarted },
            { id: "signup_step1_done", label: "Step 1 — Account done", n: fStep1, base: fStarted },
            { id: "signup_step2_done", label: "Step 2 — Funding/Skip", n: fStep2, base: fStarted },
            { id: "signup_completed", label: "Signup completed", n: fDone, base: fStarted },
          ].map((row, i, arr) => {
            const stepPct = pct(row.n, row.base || 1);
            const dropFromPrev = i > 0 ? (arr[i-1].n - row.n) : 0;
            return (
              <div key={row.id} data-testid={`funnel-${row.id}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold text-slate-700">{row.label}</span>
                  <span className="text-slate-500"><span className="font-bold text-slate-900">{row.n}</span> · {stepPct}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#87CEEB] to-[#6BB8DB] transition-all"
                    style={{ width: `${Math.max(2, stepPct)}%` }}
                  />
                </div>
                {i > 0 && dropFromPrev > 0 && (
                  <div className="flex items-center gap-1 text-[10px] text-rose-500 mt-1 ml-1">
                    <ArrowDown className="w-3 h-3" /> {dropFromPrev} dropped off
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 2-col grid: top pages + top referrers */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard title="Top pages" emptyText="No page views yet" rows={data.top_pages || []} renderRow={r => (
          <>
            <span className="text-sm font-medium text-slate-700 truncate flex-1">{r.path}</span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{r.count}</span>
          </>
        )} />
        <ListCard title="Top referrers" emptyText="No referrer data yet — most visitors so far came in directly." rows={data.top_referrers || []} renderRow={r => (
          <>
            <span className="text-sm font-medium text-slate-700 truncate flex-1" title={r.referrer}>
              {(() => { try { return new URL(r.referrer).hostname; } catch (_) { return r.referrer.slice(0, 40); } })()}
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{r.count}</span>
          </>
        )} />
      </div>

      {/* 2-col grid: top clicks + UTM sources */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard title="Top clicks" emptyText="No tracked clicks yet" rows={data.top_clicks || []} renderRow={r => (
          <>
            <span className="text-sm font-medium text-slate-700 truncate flex-1 font-mono text-xs" title={r.id}>{r.id}</span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{r.count}</span>
          </>
        )} />
        <ListCard title="Ad campaign sources (UTM)" emptyText="No UTM-tagged visits yet — append ?utm_source=reddit&utm_medium=ad&utm_campaign=launch to your ad URLs and they'll show up here." rows={data.top_utm || []} renderRow={r => (
          <>
            <span className="text-sm font-medium text-slate-700 truncate flex-1">
              <span className="font-semibold">{r.source}</span>
              {r.medium && <span className="text-slate-400"> · {r.medium}</span>}
              {r.campaign && <span className="text-slate-400"> · {r.campaign}</span>}
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{r.count}</span>
          </>
        )} />
      </div>

      {/* Device split */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Device split</h3>
        {(data.device_split || []).length === 0 ? (
          <p className="text-sm text-slate-400">No data yet.</p>
        ) : (
          <div className="flex gap-3">
            {(data.device_split || []).map(r => (
              <div key={r.label} className="flex-1 bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                {r.label === "mobile" ? <Smartphone className="w-5 h-5 text-slate-400" /> : <Monitor className="w-5 h-5 text-slate-400" />}
                <div className="flex-1">
                  <p className="text-xs text-slate-500 capitalize">{r.label}</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums leading-none mt-1">{r.count}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent event tail */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Recent events</h3>
          <span className="text-[11px] text-slate-400">Showing last {(data.recent || []).length}</span>
        </div>
        {(data.recent || []).length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No events recorded yet. Open the site in a new tab to fire one!</p>
        ) : (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-2 font-medium">Time</th>
                  <th className="px-2 py-2 font-medium">Event</th>
                  <th className="px-2 py-2 font-medium">Path</th>
                  <th className="px-2 py-2 font-medium">Session</th>
                  <th className="px-5 py-2 font-medium">Props</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent || []).map((e, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleTimeString()}</td>
                    <td className="px-2 py-2 text-xs font-mono text-[#5BA3C0]">{e.event}</td>
                    <td className="px-2 py-2 text-xs text-slate-700 truncate max-w-[180px]" title={e.path}>{e.path || "—"}</td>
                    <td className="px-2 py-2 text-xs font-mono text-slate-400">{(e.session_id || "").slice(0, 8)}</td>
                    <td className="px-5 py-2 text-xs text-slate-500 truncate max-w-[200px]" title={e.props ? JSON.stringify(e.props) : ""}>
                      {e.props ? Object.entries(e.props).slice(0, 2).map(([k, v]) => `${k}=${v}`).join(", ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-[11px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-3xl font-extrabold text-slate-900 tabular-nums mt-2 leading-none">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ListCard({ title, rows, renderRow, emptyText }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" /> {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-3">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <li key={i} className="py-2 flex items-center justify-between gap-3">
              {renderRow(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
